<template>
    <div class="product-list-page" v-if="userHasProductAdmin">
        <main-navigation></main-navigation>

        <div class="dashboard-header">
            <div class="dashboard-header__new dashboard-header__new--wide" v-click-outside="() => {this.openGroupListDropdown = false}">
                <button class="btn btn-primary" type="button" @click="openGroupListDropdown = !openGroupListDropdown">
                    <md-icon>edit</md-icon>
                    {{ $t('product_admin.page.list.button.groups') }}
                </button>
                <button
                    @click="newProduct"
                    class="btn btn-secondary"
                    type="button">
                    <md-icon>add_circle</md-icon> {{ $t('group_product.page.list.button.create') }}
                </button>
                <product-type-dropdown
                    :open="openGroupListDropdown"
                    :productTypes="productGroups"
                    @selectedType="selectedProductType">
                </product-type-dropdown>
            </div>

            <div class="dashboard-header__filters">
                <div class="product-list-page__header-search-flex">
                    <div class="search-container">
                        <search :searchData="searchData" @searchPhrase="(value) => {this.searchPhrase = value}"></search>
                    </div>
                </div>
                <div class="product-list-page__header-controls">
                    <button type="button" @click="refresh()">
                        <md-icon>refresh</md-icon>
                    </button>
                </div>
            </div>
        </div>

        <div class="filter-column">
            <group-admin-pages selected-page="GroupProductList" :group="group"></group-admin-pages>
            <basic-links position="bottom"></basic-links>
        </div>

        <div class="list-column" v-if="groupProductRows.length > 0">
            <vue-good-table
                :columns="tableColumns"
                :rows="getProductRows()"
                @on-row-click="onRowClick"
                @on-row-mouseleave="onRowMouseleave">
                <template slot="table-row" slot-scope="props">
                    <div v-if="Array.isArray(props.formattedRow[props.column.field])">
                        <div v-for="(value, index) in props.formattedRow[props.column.field]" :key="index">{{ value }}</div>
                    </div>
                    <div class="col-name" v-else-if="props.column.field === 'type' && props.row.typeOverride">
                        <span class="large-line">{{ props.row.typeOverride }}</span>
                        <span class="small-line">{{ props.row.type }}</span>
                    </div>
                    <div class="col-options" v-else-if="props.column.field === 'options' && props.row.actions.length > 0">
                        <button @click="props.row.showDropdown = !props.row.showDropdown" class="element-options">
                            <md-icon class="element-options">more_vert</md-icon>
                        </button>

                        <dropdown
                            @clicked="onDropdownClick($event, props.page)"
                            :open="props.row.showDropdown"
                            :options="props.row.actions"
                            :elementId="props.row.id"
                            width="280"
                        ></dropdown>
                    </div>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                </template>
                <div slot="emptystate">{{ $t('group_product.page.list.text.empty_state') }}</div>
            </vue-good-table>
        </div>

        <modal-group-product-edit
            :open="openGroupProductModal"
            :group-id="groupId"
            :group-product-id="selectedGroupProductId"
            @close="productEditEnd">
        </modal-group-product-edit>

        <modal-confirmation
            :open="openConfirmationDeleteModal"
            :data="confirmationDeleteModalData"
            @accepted="groupProductDelete"
            @close="openConfirmationDeleteModal = false"
        ></modal-confirmation>

        <notification></notification>

        <div class="is-loading" v-show="loading"></div>
    </div>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import {VueGoodTable} from 'vue-good-table'
import {MdIcon} from 'vue-material/dist/components'
import vClickOutside from 'v-click-outside'
import ProductTypeDropdown from '../components/dropdowns/ProductTypeDropdown.vue'

import MainNavigation from '../components/navigations/MainNavigation.vue'
import Search from '../components/Search.vue'
import ModalGroupProductEdit from '../components/modals/ModalGroupProductEdit.vue'
import Notification from '../components/Notification.vue'
import Dropdown from '../components/dropdowns/Dropdown.vue'
import ModalConfirmation from '../components/modals/ModalConfirmation'
import GroupAdminPages from "@/components/sidebars/GroupAdminPages";
import BasicLinks from '../components/BasicLinks.vue'

Vue.use(MdIcon)
Vue.use(VueGoodTable)

export default {
    name: 'GroupProductList',

    data() {
        return {
            loading: true,
            openGroupProductModal: false,
            selectedGroupProductId: null,

            openGroupListDropdown: false,
            productGroups: [],

            searchData: {
                placeholder: this.$t('group_product.page.list.search.placeholder'),
                tooltipText: this.$t('group_product.page.list.search.tooltip'),
                countText: ''
            },

            searchPhrase: '',

            deleteGroupProductId: null,
            openConfirmationDeleteModal: false,
            confirmationDeleteModalData: {
                acceptText: this.$t('group_product.confirmation.delete.ok'),
                cancelText: this.$t('group_product.confirmation.delete.cancel'),
                message: this.$t('group_product.confirmation.delete.question')
            },

            tableColumns: [
                {
                    label: this.$t('group_product.page.list.column.name'),
                    field: 'name',
                    width: '20%',
                },
                {
                    label: this.$t('group_product.page.list.column.product_number'),
                    field: 'productNumber',
                    width: '20%',
                },
                {
                    label: this.$t('group_product.page.list.column.type'),
                    field: 'type',
                    width: '20%',
                },
                {
                    label: '',
                    field: 'options',
                    width: '6%'
                }
            ]
        }
    },

    mounted() {
        this.$store.dispatch('FETCH_CURRENT_USER').then(() => {
            if (this.userHasProductAdmin) {
                this.$store.dispatch('FETCH_PRODUCT_GROUPS').then(data => {
                    this.productGroups = data;
                    this.fetchData()
                })
            } else {
                this.$router.push('/')
            }
        }).catch((redirect) => {
            redirect && this.$router.push('/');
        })
    },

    watch: {
        '$route'() {
            this.$store.commit('resetGroupProductList')
            this.fetchData()
        }
    },

    methods: {
        getProductRows() {
            let tableData = this.groupProductRows, searchPhrase

            if (this.searchPhrase !== '') {
                searchPhrase = this.searchPhrase.toLowerCase().split(/\s+/)
                tableData = tableData.filter(row => {
                    let valid = true
                    for (let value of searchPhrase) {
                        valid = valid && row.search.toLowerCase().indexOf(value) > -1
                    }
                    return valid
                })
            }

            this.searchData.countText = this.$tc('group_product.page.list.search.name', tableData.length)

            return tableData
        },

        newProduct() {
            this.selectedGroupProductId = null
            this.openGroupProductModal = true
        },

        onRowMouseleave(el) {
            el.row.showDropdown = false
        },

        onRowClick(params) {
            if (!params.event.target.className.includes('element-options')) {
                params.row.showDropdown = false
                this.selectedGroupProductId = params.row.id
                this.openGroupProductModal = true
            }
        },

        onDropdownClick(val) {
            switch (val.action.action) {
            case 'delete':
                this.deleteGroupProductId = val.id
                this.openConfirmationDeleteModal = true
                break
            }
        },

        groupProductDelete() {
            let payload = {
                groupId: this.$route.params.groupId,
                groupProductId: this.deleteGroupProductId
            }

            this.$store.dispatch('DELETE_GROUP_PRODUCT', payload).then(() => {
                this.$store.commit('setNotification', [this.$t('group_product.page.list.confirmation.delete.notification.success')])
                this.deleteGroupProductId = null
                this.refresh()
            }).catch(() => {
                this.$store.commit('setNotification', [this.$t('group_product.page.list.confirmation.delete.notification.failed')])
            }).finally(() => {
                this.openConfirmationDeleteModal = false
            })
        },

        fetchData() {
            this.$store.dispatch('FETCH_GROUP_PRODUCTS', this.$route.params.groupId).then(data => {
                this.loading = false;
            })
        },

        productEditEnd(refresh) {
            this.openGroupProductModal = false
            if (refresh) {
                this.refresh()
            }
        },

        selectedProductType(payload) {
            this.openGroupProductModal = false
            this.openGroupListDropdown = false
            this.$router.push({name: 'GroupAdmin', params: {groupId: payload.id}})
        },

        refresh() {
            this.loading = true
            this.fetchData()
        }
    },

    directives: {
        clickOutside: vClickOutside.directive
    },

    computed: {
        groupId() {
            return this.$route.params.groupId
        },

        group() {
            return this.productGroups.find(group => group.id === Number(this.groupId))
        },

        ...mapGetters(['groupProductRows', 'userHasProductAdmin'])
    },

    components: {
        MainNavigation,
        VueGoodTable,
        ProductTypeDropdown,
        Search,
        ModalGroupProductEdit,
        Notification,
        Dropdown,
        BasicLinks,
        GroupAdminPages,
        ModalConfirmation
    }
}
</script>

<style lang="scss">
@import '../assets/sass/ui/tooltip.scss';
@import '../assets/sass/ui/vuetable.scss';
@import '../assets/sass/ui/loading.scss';
</style>

<style lang="scss" scoped>
@import '../assets/sass/toolkit.scss';
@import '../assets/sass/ui/input.scss';
@import '../assets/sass/ui/button.scss';
@import '../assets/sass/partials/dashboard-header.scss';
@import '../assets/sass/layout/list-page.scss';

.product-list-page {
    .dashboard-header__new {
        &--wide {
            flex-basis: 400px;
        }

        .btn-secondary {
            margin-left: 10px;
            width: 165px;
        }
    }

    .dashboard-header {
        .search-container {
            width: 100%;
            max-width: 640px;
        }

        .checkbox {
            margin-left: auto;
            margin-right: 32px;
        }

        .filter-component {
            margin-left: 15px;
        }
    }

    &__header-search-flex {
        display: flex;
        align-items: center;
        flex-basis: calc(100% - 70px);
    }

    &__header-controls {
        padding-right: 30px;
    }

    .list-column {
        font-weight: 500;
    }

    .filter-column {
        max-width: calc(100vh - 140px);
        overflow-x: hidden;
        overflow-y: auto;
    }
}
</style>
